/**
 * CSV export for Intelligence drill-through tables. Used by Phase 3
 * blocks (stealth watchlist, ecosystem snapshot, stage mix, founder
 * pedigree) — not retrofitted to v1 blocks.
 *
 * RFC 4180 quoting:
 *  - Fields containing `,` `"` `\n` `\r` are wrapped in double quotes.
 *  - Existing `"` inside a quoted field is escaped as `""`.
 *  - Numbers, null, undefined, booleans are coerced to string; null /
 *    undefined become empty cells (not the literal "null").
 *
 * Formula-injection mitigation:
 *  - Cells whose first character is `=`, `+`, `-`, or `@` get a
 *    leading apostrophe prefix. Excel and Google Sheets interpret
 *    those leading characters as formulas when a user opens the CSV,
 *    so a fund name like `=cmd|"/c calc.exe"!A0` would execute on
 *    open without this guard. Intel data is reachable by any
 *    signed-in user, and exports like stealth watchlist include raw
 *    `all_investors` strings which are unconstrained text[] coming
 *    from third-party providers — a single poisoned investor name
 *    could turn a CSV download into code execution on an analyst's
 *    machine.
 */

export type CsvCell = string | number | boolean | null | undefined;

// Formula-injection lead pattern — matches `=`, `+`, `-`, `@`
// OPTIONALLY preceded by ANY whitespace. Excel / Google Sheets
// strip leading whitespace before formula evaluation, so
// `\t=SUM(...)`, `\r=...`, and ` =...` all still execute on CSV
// open despite having a non-formula first byte. `\s` in JS regex
// covers space, tab, CR, LF, vertical-tab, form-feed, and the
// Unicode whitespace class — the exact set spreadsheet apps ignore.
const FORMULA_LEAD_PATTERN = /^\s*[=+\-@]/;

function escapeCell(value: CsvCell): string {
  if (value == null) return "";
  let s = typeof value === "string" ? value : String(value);
  // Defuse formula-injection BEFORE RFC 4180 quoting so the
  // apostrophe prefix survives into the output cell.
  if (typeof value === "string" && FORMULA_LEAD_PATTERN.test(s)) {
    s = `'${s}`;
  }
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function rowsToCsv(headers: string[], rows: CsvCell[][]): string {
  const lines: string[] = [];
  lines.push(headers.map(escapeCell).join(","));
  for (const row of rows) {
    lines.push(row.map(escapeCell).join(","));
  }
  // Use \r\n per RFC 4180 — Excel on Windows is picky.
  return lines.join("\r\n");
}

/**
 * Trigger a browser download of `csv` as `filename`. Uses the
 * Blob + object-URL pattern so the content lives only long enough
 * for the download to kick off, then revokes.
 */
export function downloadCsv(csv: string, filename: string): void {
  // Prepend UTF-8 BOM so Excel opens non-ASCII characters correctly
  // (fund names, category labels often contain Unicode).
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Convenience wrapper — build a CSV from headers + rows and download
 * it in one call. Use this from component export buttons.
 */
export function exportCsv(filename: string, headers: string[], rows: CsvCell[][]): void {
  downloadCsv(rowsToCsv(headers, rows), filename);
}
